/**
 * The web app's identity: marketing version plus GitHub build stamp.
 *
 * `version.json` beside src/ is a generated copy of /version.json, the single
 * marketing version shared with the server and the mobile app (`npm run
 * version:sync` writes it; CI fails on drift). The build stamp is injected at
 * build time by scripts/build.js.
 *
 * See docs/operations/versioning-and-build-stamps.md.
 */
import versionInfo from "../version.json";

function clean(value) {
  const normalized = `${value || ""}`.trim();
  return normalized || null;
}

function parseBuildNumber(value) {
  const raw = `${value ?? ""}`.trim();
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export const APP_VERSION = versionInfo.version;
export const APP_GIT_SHA = clean(process.env.REACT_APP_UNIFLO_GIT_SHA);
export const APP_BUILD_NUMBER = parseBuildNumber(process.env.REACT_APP_UNIFLO_BUILD_NUMBER);

/** "1.0.1+1755" when the build number is known, "1.0.1+g7e21f4b" otherwise. */
export function formatDisplayVersion({ version, buildNumber, gitShortSha } = {}) {
  if (buildNumber) return `${version}+${buildNumber}`;
  if (gitShortSha) return `${version}+g${gitShortSha}`;
  return `${version}`;
}

/** The human form: "1.0.1 (build 1755)" or "1.0.1 (7e21f4b)". */
export function formatVersionLabel({ version, buildNumber, gitShortSha } = {}) {
  if (buildNumber) return `${version} (build ${buildNumber})`;
  if (gitShortSha) return `${version} (${gitShortSha})`;
  return `${version}`;
}

export const APP_RELEASE = Object.freeze({
  version: APP_VERSION,
  buildNumber: APP_BUILD_NUMBER,
  gitShortSha: APP_GIT_SHA,
  displayVersion: formatDisplayVersion({
    version: APP_VERSION,
    buildNumber: APP_BUILD_NUMBER,
    gitShortSha: APP_GIT_SHA,
  }),
  label: formatVersionLabel({
    version: APP_VERSION,
    buildNumber: APP_BUILD_NUMBER,
    gitShortSha: APP_GIT_SHA,
  }),
});

/**
 * Make the running bundle identifiable from outside the app: a support agent,
 * a QA script, or a deploy check can read
 * `document.querySelector('meta[name="uniflo-version"]').content` instead of
 * guessing from bundle hashes whether a deploy has landed.
 */
export function publishAppVersion(release = APP_RELEASE, doc = document, win = window) {
  let meta = doc.querySelector('meta[name="uniflo-version"]');
  if (!meta) {
    meta = doc.createElement("meta");
    meta.setAttribute("name", "uniflo-version");
    doc.head.appendChild(meta);
  }
  meta.setAttribute("content", release.displayVersion);
  doc.documentElement.dataset.unifloVersion = release.version;
  // eslint-disable-next-line no-param-reassign
  win.__UNIFLO_RELEASE__ = release;
  return release;
}
